import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Paper,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  Popover,
} from '@mui/material';
import { Dialog, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PreviewIcon from '@mui/icons-material/Preview';
import InfoIcon from '@mui/icons-material/Info';
import { getDocumentoId, putDocumento, marcarParaReenvio } from '../../componentes/http/actions/DocumentoAction';
import { postTipoDocumentos } from "../../componentes/http/actions/TipoDocumentoAction";
import { postOrganismoEmisor } from "../../componentes/http/actions/OrganismoEmisorAction";
import { postEstadoTramitacion } from "../../componentes/http/actions/EstadoTramitacionAction";
import { postClasificacionDocumentos } from "../../componentes/http/actions/ClasificacionDocumentoAction";
import { postRubro } from "../../componentes/http/actions/RubroAction";
import { postPais } from "../../componentes/http/actions/PaisAction";
import { postClientes } from "../../componentes/http/actions/ClientesAction";
import DataLoader from '../../componentes/data/DataLoader';
import CheckboxGroup from '../../componentes/data/CheckBox';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import AutocompleteWithAddButton from "../../componentes/common/AutocompleteAddButton";
import MantenedorQuickAddModal from "../../componentes/common/MantenedorAddModal";
import { NotificacionContext } from '../../componentes/common/NotificacionContext';
import ReenvioNormativaDialog from '../../componentes/modal/Normativa/ReenvioNormativaDialog';

const EditarNormativa = () => {
  // Definimos un punto de quiebre para reorganizar el formulario
  const isSmallScreen = useMediaQuery('(max-width:1200px)');

  const [clientesSeleccionadosReenvio, setClientesSeleccionadosReenvio] = useState([]);

  const { mostrarNotificacion } = useContext(NotificacionContext);
  const [normativaFueEnviada, setNormativaFueEnviada] = useState(false);
  const [mostrarDialogoReenvio, setMostrarDialogoReenvio] = useState(false);
  const [mostrarDialogoReenvioInmediato, setMostrarDialogoReenvioInmediato] = useState(false);
  const [estadosEnvioClientes, setEstadosEnvioClientes] = useState([]);
  const [anchorElEstados, setAnchorElEstados] = useState(null);

  const { id } = useParams();
  const navigate = useNavigate();
  
  // Referencia al DataLoader para poder recargar datos
  const dataLoaderRef = useRef(null);
  
  const [formData, setFormData] = useState({
    id: '',
    nombre: '',
    nombreAbreviado: '',
    descripcion: '',
    descripcionAbreviado: '',
    vigencia: '',
    codigo: '',
    url: '',
    enlaceResumen: '',
    creadoPor: '',
    organismoEmisorId: '',
    tipoDocumentoId: '',
    estadoTramitacionId: '',
    paisId: 0,
    fechaPublicacion: null,
    actualizacionNorma: false,
    rubroId: [],
    clasificacionDocumentoId: [],
    clienteId: [],
    habilitado: true,
  });
  const [fecha, setFecha] = useState(null);

  const [loadedData, setLoadedData] = useState({
    organismos: [],
    tiposDocumento: [],
    estadosTramitacion: [],
    rubros: [],
    clasificaciones: [],
    clientes: [],
    paises: []
  });

  // Estado para tipos de documento filtrados
  const [tiposDocumentoFiltrados, setTiposDocumentoFiltrados] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Estados para controlar la apertura de los modales de añadir
  const [modalState, setModalState] = useState({
    tipoDocumento: false,
    organismoEmisor: false,
    estadoTramitacion: false,
    rubro: false,
    clasificacion: false,
    cliente: false,
    pais: false,
  });
  
  const [validationErrors, setValidationErrors] = useState({
      rubros: false,
      clientes: false,
      clasificaciones: false
    });
  
  const [quickAddLoading, setQuickAddLoading] = useState(false);

  const truncarTexto = (texto, maxLength = 50) => {
    return texto.length > maxLength 
      ? texto.substring(0, maxLength) + "..." 
      : texto;
  };

  // Función para obtener estadísticas de envío
  const getEstadisticasEnvio = () => {
    const enviados = estadosEnvioClientes.filter(cliente => cliente.fueEnviado).length;
    const total = estadosEnvioClientes.length;
    return { enviados, total, pendientes: total - enviados };
  };

  // Función para filtrar tipos de documento basado en organismo seleccionado
  const filtrarTiposDocumento = useCallback((organismoId) => {
    if (!organismoId || !loadedData.organismos || !loadedData.tiposDocumento) {
      setTiposDocumentoFiltrados(loadedData.tiposDocumento || []);
      return;
    }

    const organismoSeleccionado = loadedData.organismos.find(org => org.id === organismoId);
    
    if (organismoSeleccionado && organismoSeleccionado.idTipoDocumento && organismoSeleccionado.idTipoDocumento.length > 0) {
      const tiposFiltrados = loadedData.tiposDocumento.filter(tipo => 
        organismoSeleccionado.idTipoDocumento.includes(tipo.id)
      );
      setTiposDocumentoFiltrados(tiposFiltrados);
    } else {
      setTiposDocumentoFiltrados(loadedData.tiposDocumento || []);
    }
  }, [loadedData.organismos, loadedData.tiposDocumento]);

  const validateForm = () => {
    const errors = {
      rubros: formData.rubroId.length === 0,
      clientes: formData.clienteId.length === 0,
      clasificaciones: formData.clasificacionDocumentoId.length === 0
    };
    
    setValidationErrors(errors);
    
    const hasBasicErrors = !formData.nombre || !fecha || !formData.tipoDocumentoId || 
      !formData.organismoEmisorId || !formData.estadoTramitacionId || !formData.url || !formData.descripcion;
    
    return !hasBasicErrors && !errors.rubros && !errors.clientes;
  };

  useEffect(() => {
    getDocumentoId(localStorage.getItem('token'), id)
      .then(data => {
        setFormData(prevData => ({
          ...prevData,
          ...data,
          id: data.id,
          nombre: data.nombre || '',
          nombreAbreviado: data.nombreAbreviado || '',
          descripcion: data.descripcion || '',
          descripcionAbreviado: data.descripcionAbreviado || '',
          vigencia: data.vigencia || 'Desde su publicación',
          codigo: data.codigo || '',
          url: data.url || '',
          enlaceResumen: data.enlaceResumen || '',
          creadoPor: data.creadoPor || '',
          organismoEmisorId: data.organismoEmisorId || '',
          tipoDocumentoId: data.tipoDocumentoId || '',
          estadoTramitacionId: data.estadoTramitacionId || '',
          paisId: data.paisId || 0,
          fechaPublicacion: data.fechaPublicacion ? new Date(data.fechaPublicacion) : null,
          actualizacionNorma: Boolean(data.actualizacionNorma),
          rubroId: Array.isArray(data.rubros) ? data.rubros.map(rubro => rubro.id) : [],
          clasificacionDocumentoId: Array.isArray(data.clasificacionDocumentos) ? data.clasificacionDocumentos.map(clasificacion => clasificacion.id) : [],
          clienteId: Array.isArray(data.clientes) ? data.clientes.map(cliente => cliente.id) : []
        }));
        setFecha(data.fechaPublicacion ? new Date(data.fechaPublicacion) : null);
        setNormativaFueEnviada(data.fueEnviada || false);
        setEstadosEnvioClientes(data.estadosEnvioClientes || []);
        setIsLoading(false);
      })
      .catch(error => {
        console.error("Error al cargar la normativa:", error);
        mostrarNotificacion("Error al cargar la normativa", "error");
        setIsLoading(false);
      });
  }, [id, mostrarNotificacion]);

  const handleDataLoaded = useCallback((data) => {
    setLoadedData(data);
    setTiposDocumentoFiltrados(data.tiposDocumento || []);
    
    if (formData.organismoEmisorId) {
      setTimeout(() => {
        const organismoSeleccionado = data.organismos.find(org => org.id === formData.organismoEmisorId);
        if (organismoSeleccionado && organismoSeleccionado.idTipoDocumento && organismoSeleccionado.idTipoDocumento.length > 0) {
          const tiposFiltrados = data.tiposDocumento.filter(tipo => 
            organismoSeleccionado.idTipoDocumento.includes(tipo.id)
          );
          setTiposDocumentoFiltrados(tiposFiltrados);
        }
      }, 100);
    }
  }, [formData.organismoEmisorId]);

  const handleChange = useCallback((event) => {
    const { name, value, checked, type } = event.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleOrganismoChange = useCallback((event, newValue) => {
    const organismoId = newValue ? newValue.id : "";
    
    setFormData((prevData) => ({
      ...prevData,
      organismoEmisorId: organismoId,
      tipoDocumentoId: organismoId ? prevData.tipoDocumentoId : "",
    }));

    filtrarTiposDocumento(organismoId);

    if (formData.tipoDocumentoId && organismoId) {
      const organismoSeleccionado = loadedData.organismos.find(org => org.id === organismoId);
      if (organismoSeleccionado && 
          organismoSeleccionado.idTipoDocumento && 
          organismoSeleccionado.idTipoDocumento.length > 0 &&
          !organismoSeleccionado.idTipoDocumento.includes(formData.tipoDocumentoId)) {
        setFormData((prevData) => ({
          ...prevData,
          tipoDocumentoId: "",
        }));
      }
    }
  }, [formData.tipoDocumentoId, loadedData.organismos, filtrarTiposDocumento]);

  const handleCheckboxChange = useCallback((event, id) => {
    const { name, checked } = event.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: checked
        ? [...prevData[name], id]
        : prevData[name].filter(item => item !== id)
    }));

    if (checked) {
      if (name === 'rubroId') {
        setValidationErrors(prev => ({ ...prev, rubros: false }));
      } else if (name === 'clienteId') {
        setValidationErrors(prev => ({ ...prev, clientes: false }));
      }
    }
  }, []);
  
  const handleOpenModal = (modalName) => {
    setModalState(prev => ({
      ...prev,
      [modalName]: true
    }));
  };
  
  const handleCloseModal = (modalName) => {
    setModalState(prev => ({
      ...prev,
      [modalName]: false
    }));
  };
  
  const reloadData = () => {
    if (dataLoaderRef.current && typeof dataLoaderRef.current.loadData === 'function') {
      dataLoaderRef.current.loadData();
      setTimeout(() => {
        if (formData.organismoEmisorId) {
          filtrarTiposDocumento(formData.organismoEmisorId);
        }
      }, 100);
    }
  };
  
  // Handlers para crear nuevos elementos
  const handleAddTipoDocumento = async (data) => {
    try {
      setQuickAddLoading(true);
      await postTipoDocumentos(localStorage.getItem("token"), data);
      reloadData();
      handleCloseModal('tipoDocumento');
      mostrarNotificacion(`Tipo de documento "${data.nombre}" agregado con éxito`, "success");
    } catch (error) {
      console.error("Error al agregar tipo de documento:", error);
      mostrarNotificacion("Error al agregar tipo de documento", "error");
    } finally {
      setQuickAddLoading(false);
    }
  };
  
  const handleAddOrganismoEmisor = async (data) => {
    try {
      setQuickAddLoading(true);
      await postOrganismoEmisor(localStorage.getItem("token"), data);
      reloadData();
      handleCloseModal('organismoEmisor');
      mostrarNotificacion(`Organismo emisor "${data.nombre}" agregado con éxito`, "success");
    } catch (error) {
      console.error("Error al agregar organismo emisor:", error);
      mostrarNotificacion("Error al agregar organismo emisor", "error");
    } finally {
      setQuickAddLoading(false);
    }
  };
  
  const handleAddEstadoTramitacion = async (data) => {
    try {
      setQuickAddLoading(true);
      await postEstadoTramitacion(localStorage.getItem("token"), data);
      reloadData();
      handleCloseModal('estadoTramitacion');
      mostrarNotificacion(`Estado de tramitación "${data.nombre}" agregado con éxito`, "success");
    } catch (error) {
      console.error("Error al agregar estado de tramitación:", error);
      mostrarNotificacion("Error al agregar estado de tramitación", "error");
    } finally {
      setQuickAddLoading(false);
    }
  };
  
  const handleAddClasificacion = async (data) => {
    try {
      setQuickAddLoading(true);
      await postClasificacionDocumentos(localStorage.getItem("token"), data);
      reloadData();
      handleCloseModal('clasificacion');
      mostrarNotificacion(`Clasificación "${data.nombre}" agregada con éxito`, "success");
    } catch (error) {
      console.error("Error al agregar clasificación:", error);
      mostrarNotificacion("Error al agregar clasificación", "error");
    } finally {
      setQuickAddLoading(false);
    }
  };
  
  const handleAddRubro = async (data) => {
    try {
      setQuickAddLoading(true);
      await postRubro(localStorage.getItem("token"), data);
      reloadData();
      handleCloseModal('rubro');
      mostrarNotificacion(`Rubro "${data.nombre}" agregado con éxito`, "success");
    } catch (error) {
      console.error("Error al agregar rubro:", error);
      mostrarNotificacion("Error al agregar rubro", "error");
    } finally {
      setQuickAddLoading(false);
    }
  };

  const handleAddPais = async (data) => {
      try {
        setQuickAddLoading(true);
        await postPais(localStorage.getItem("token"), data);
  
        // Recargar datos usando el DataLoader
        reloadData();
        handleCloseModal('pais');
        mostrarNotificacion (`País "${data.nombre}" agregado con éxito`, "success");
      } catch (error) {
        console.error("Error al agregar país:", error);
        mostrarNotificacion("Error al agregar país", "error");
      } finally {
        setQuickAddLoading(false);
      }
    };


  const handleAbrirDialogoReenvioInmediato = () => {   
    setMostrarDialogoReenvioInmediato(true);
  };

  const handleReenvioInmediatoExitoso = (resultado) => {
  // Actualizar el estado de la normativa después del reenvío exitoso
  mostrarNotificacion(
    `Reenvío completado exitosamente: ${resultado.clientesExitosos} de ${resultado.totalClientes} clientes`,
    "success"
  );
  
  // Recargar los datos de la normativa para actualizar los estados
  getDocumentoId(localStorage.getItem('token'), id)
    .then(data => {
      setEstadosEnvioClientes(data.estadosEnvioClientes || []);
      setNormativaFueEnviada(data.fueEnviada || false);
    })
    .catch(error => {
      console.error("Error al recargar datos:", error);
    });
};

  const handleAbrirDialogoReenvio = () => {   
    setClientesSeleccionadosReenvio([]);
    setMostrarDialogoReenvio(true);
  };

  const handleSeleccionarClienteReenvio = (clienteId, isSelected) => {
    if (isSelected) {
      setClientesSeleccionadosReenvio(prev => [...prev, clienteId]);
    } else {
      setClientesSeleccionadosReenvio(prev => prev.filter(id => id !== clienteId));
    }
  };

  const handleSeleccionarTodosClientes = (event) => {
    if (event.target.checked) {
      // Seleccionar todos los clientes que fueron enviados
      const todosClientesEnviados = estadosEnvioClientes
        .filter(cliente => cliente.fueEnviado)
        .map(cliente => cliente.clienteId);
      setClientesSeleccionadosReenvio(todosClientesEnviados);
    } else {
      setClientesSeleccionadosReenvio([]);
    }
  };

  const handleAddCliente = async (data) => {
    try {
      setQuickAddLoading(true);
      await postClientes(localStorage.getItem("token"), data);
      reloadData();
      handleCloseModal('cliente');
      mostrarNotificacion(`Cliente "${data.nombre}" agregado con éxito`, "success");
    } catch (error) {
      console.error("Error al agregar cliente:", error);
      mostrarNotificacion("Error al agregar cliente", "error");
    } finally {
      setQuickAddLoading(false);
    }
  };

  const handleReenviarNormativa = async () => {
    if (clientesSeleccionadosReenvio.length === 0) {
      mostrarNotificacion("Debe seleccionar al menos un cliente para reenvío", "warning");
      return;
    }

    try {
      await marcarParaReenvio(localStorage.getItem('token'), id, clientesSeleccionadosReenvio);
      setNormativaFueEnviada(false);
      setEstadosEnvioClientes([]);
      setMostrarDialogoReenvio(false);
      setClientesSeleccionadosReenvio([]);
      mostrarNotificacion("Normativa marcada para reenvío exitosamente", "success");
      
      // Recargar datos para actualizar estados
      const data = await getDocumentoId(localStorage.getItem('token'), id);
      setEstadosEnvioClientes(data.estadosEnvioClientes || []);
      setNormativaFueEnviada(data.fueEnviada || false);
    } catch (error) {
      mostrarNotificacion("Error al marcar para reenvío", "error");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      mostrarNotificacion("Por favor, complete todos los campos obligatorios", "error");
      return;
    }
    
    setIsSaving(true);
    try {
      const dataToSubmit = {
        ...formData,
        fechaPublicacion: fecha ? fecha.toISOString() : null,
        actualizacionNorma: formData.actualizacionNorma,
      };
      const response = await putDocumento(localStorage.getItem('token'), dataToSubmit);
      const nombreTruncado = truncarTexto(formData.nombre, 60);
      const mensajeExito = `La normativa "${nombreTruncado}" ha sido actualizada con éxito`;
      
      mostrarNotificacion(mensajeExito || response.message, "success");
      navigate('/gestor-normativa')
    } catch (error) {
      console.error("Error al actualizar normativa:", error);
      mostrarNotificacion("Error al actualizar normativa", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Definir los campos para cada tipo de mantenedor
  const tipoDocumentoFields = [
    { name: 'nombre', label: 'Nombre', required: true },
  ];
  
  const organismoEmisorFields = [
    { name: 'nombre', label: 'Nombre', required: true },
    { name: 'sigla', label: 'Sigla', required: true },
    { name: 'nombreAbreviado', label: 'Nombre Abreviado', required: true },
  ];
  
  const estadoTramitacionFields = [
    { name: 'nombre', label: 'Nombre', required: true },
  ];
  
  const clasificacionFields = [
    { name: 'nombre', label: 'Nombre', required: true },
  ];
  
  const rubroFields = [
    { name: 'nombre', label: 'Nombre', required: true },
  ];
  
  const clienteFields = [
    { name: 'nombre', label: 'Nombre', required: true },
    { name: 'tipoCorreo', label: 'Tipo de Correo', required: true },
  ];

  const paisFields = [
    { name: 'nombre', label: 'Nombre', required: true },
    { name: 'codigoISO', label: 'Código ISO', required: true },
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const { enviados, total } = getEstadisticasEnvio();

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{paddingTop: {xs: '20px', sm: '10px'}}}>
      <DataLoader 
        onDataLoaded={handleDataLoaded} 
        ref={dataLoaderRef}
      />
      
      {/* Header con título y estado detallado */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
          Editar Normativa
        </Typography>
        
        {/* Status detallado de envío */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          flexWrap: 'wrap'
        }}>
          {/* Status clickeable con detalles */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: normativaFueEnviada ? 'success.light' : 'info.light',
              color: "#fff",
              border: 1,
              borderColor: normativaFueEnviada ? 'success.main' : 'info.main',
              cursor: total > 0 ? 'pointer' : 'default'
            }}
            onClick={(e) => total > 0 && setAnchorElEstados(e.currentTarget)}
          >
            <Box sx={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%',
              backgroundColor: normativaFueEnviada ? 'success.main' : 'info.main'
            }} />
            <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '1rem' }}>
              {total > 0 
                ? `Enviado: ${enviados}/${total} clientes`
                : 'Sin clientes asignados'
              }
            </Typography>
            {total > 0 && (
              <InfoIcon sx={{ fontSize: '1rem', ml: 0.5 }} />
            )}
          </Box>
          
          {/* Popover con detalles de envío */}
          <Popover
            open={Boolean(anchorElEstados)}
            anchorEl={anchorElEstados}
            onClose={() => setAnchorElEstados(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <Box sx={{ p: 2, maxWidth: 350 }}>
              <Typography variant="h6" gutterBottom>
                Estado de Envío por Cliente
              </Typography>
              <List dense>
                {estadosEnvioClientes.map((cliente) => (
                  <ListItem key={cliente.clienteId} sx={{ px: 0 }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%',
                      backgroundColor: cliente.fueEnviado ? 'success.main' : 'warning.main',
                      mr: 1,
                      mt: 0.5
                    }} />
                    <ListItemText
                      primary={cliente.clienteNombre}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Estado: {cliente.estadoEnvio}
                          </Typography>
                          {cliente.fechaEnvio && (
                            <Typography variant="body2" color="text.secondary">
                              Enviado: {new Date(cliente.fechaEnvio).toLocaleDateString('es-ES')}
                            </Typography>
                          )}
                          {cliente.fechaProgramada && !cliente.fueEnviado && (
                            <Typography variant="body2" color="text.secondary">
                              Programado: {new Date(cliente.fechaProgramada).toLocaleDateString('es-ES')}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Popover>
          
          {/* Botón de reenvío */}
          {normativaFueEnviada && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={handleAbrirDialogoReenvio}
                sx={{ minWidth: 'auto' }}
              >
                Marcar para Reenvío
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleAbrirDialogoReenvioInmediato}
                sx={{ minWidth: 'auto' }}
              >
                Reenviar Ahora
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      
      <Grid container spacing={2} elevation={3}>
        <Grid item xs={12} md={isSmallScreen ? 12 : 8}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={isSmallScreen ? 12 : 6}>
              <TextField
                fullWidth
                label="Nombre"
                name="nombre"
                value={formData.nombre}
                inputProps={{ maxLength: 100}}
                helperText={`${formData.nombre.length}/100 caracteres`}
                onChange={handleChange}
                required={true}
              />
            </Grid>
            <Grid item xs={12} sm={isSmallScreen ? 12 : 6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  sx={{ width: "100%" }}
                  format="dd/MM/yyyy"
                  label="Fecha de publicación"
                  value={fecha}
                  onChange={(date) => setFecha(date)}
                  slotProps={{
                    textField: {
                      required: true,
                      fullWidth: true
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={isSmallScreen ? 12 : 6}>
              <AutocompleteWithAddButton
                options={loadedData.organismos}
                label="Organismo emisor"
                required={true}
                value={
                  loadedData.organismos.find(
                    (org) => org.id === formData.organismoEmisorId
                  ) || null
                }
                sx={{ mt: -1}}
                onChange={handleOrganismoChange}
                onAddNewClick={() => handleOpenModal('organismoEmisor')}
              />
            </Grid>
            <Grid item xs={12} sm={isSmallScreen ? 12 : 6}>
              <Autocomplete
                fullWidth
                options={tiposDocumentoFiltrados}
                getOptionLabel={(option) => option.nombre || ''}
                value={
                  tiposDocumentoFiltrados.find(
                    (tipo) => tipo.id === formData.tipoDocumentoId
                  ) || null
                }
                onChange={(event, newValue) => {
                  handleChange({
                    target: {
                      name: "tipoDocumentoId",
                      value: newValue ? newValue.id : "",
                    },
                  });
                }}
                disabled={!formData.organismoEmisorId}
                sx={{ mt: 1, mb: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tipo de Documento"
                    required={true}
                    helperText={!formData.organismoEmisorId ? "Primero selecciona un organismo emisor" : ""}
                  />
                )}
                noOptionsText="No hay tipos de documento disponibles"
              />
            </Grid>
            <Grid item xs={12} sm={isSmallScreen ? 12 : 6}>
              <AutocompleteWithAddButton
                options={loadedData.estadosTramitacion}
                label="Estado de tramitación"
                required={true}
                value={
                  loadedData.estadosTramitacion.find(
                    (estado) => estado.id === formData.estadoTramitacionId
                  ) || null
                }
                sx={{ mt: -2 }}
                onChange={(event, newValue) => {
                  handleChange({
                    target: {
                      name: "estadoTramitacionId",
                      value: newValue ? newValue.id : "",
                    },
                  });
                }}
                onAddNewClick={() => handleOpenModal('estadoTramitacion')}
              />
            </Grid>

            <Grid item xs={12} sm={isSmallScreen ? 12 : 6}>
             <TextField
               fullWidth
               label="Vigencia"
               name="vigencia"
               value={formData.vigencia}
               onChange={handleChange}
               required={true}
             />
           </Grid>

            <Grid item xs={12} sm={isSmallScreen ? 12 : 6}>
              <TextField
                fullWidth
                label="URL"
                name="url"
                value={formData.url}
                onChange={handleChange}
                required={true}
              />
            </Grid>
            
            <Grid item xs={12} sm={isSmallScreen ? 12 : 6}>
              <TextField
                fullWidth
                label="Enlace resumen"
                name="enlaceResumen"
                value={formData.enlaceResumen}
                onChange={handleChange}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={isSmallScreen ? 8 : 6}
                label="Descripción"
                name="descripcion"
                value={formData.descripcion}
                onChange={handleChange}
                required={true}
              />
            </Grid>
            <Grid item xs={12} sm={isSmallScreen ? 12 : 6}>
                <AutocompleteWithAddButton
                  options={loadedData.paises}
                  label="País"
                  required={true}
                  value={
                    loadedData.paises.find(
                      (pais) => pais.id === formData.paisId
                    ) || null
                  }
                  sx={{ mt: -1 }}
                  onChange={(event, newValue) => {
                    handleChange({
                      target: {
                        name: "paisId",
                        value: newValue ? newValue.id : "",
                      },
                    });
                  }}
                  onAddNewClick={() => handleOpenModal('pais')}
                />
              </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.actualizacionNorma}
                    onChange={(e) => handleChange(e)}
                    name="actualizacionNorma"
                    type="checkbox"
                  />
                }
                label="Es actualización de normativa"
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={isSmallScreen ? 12 : 4}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: { xs: 2, sm: 3 }, 
              mb: 3, 
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6">Aplica a</Typography>
              <Button 
                size="small" 
                color="primary" 
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenModal('clasificacion')}
              >
                Añadir
              </Button>
            </Box>
            <CheckboxGroup
              title=""
              items={loadedData.clasificaciones}
             selectedItems={formData.clasificacionDocumentoId}
             onChange={handleCheckboxChange}
             name="clasificacionDocumentoId"
           />
         </Paper>
       </Grid>
     </Grid>

     <Grid container spacing={2} elevation={3}>
       <Grid item xs={12} md={isSmallScreen ? 12 : 6} lg={isSmallScreen ? 12 : 4}>
         <Paper 
           elevation={3} 
           sx={{ 
             p: { xs: 2, sm: 3 }, 
             mb: 3, 
             borderRadius: 3,
             border: '1px solid',
             borderColor: validationErrors.rubros ? 'error.main' : 'divider'
           }}
         >
           <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
             <Typography variant="h6" color={validationErrors.rubros ? "error" : "inherit"}>
               Rubros
               <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
             </Typography>
             <Button 
               size="small" 
               color="primary" 
               variant="contained"
               startIcon={<AddIcon />}
               onClick={() => handleOpenModal('rubro')}
             >
               Añadir
             </Button>
           </Box>
           <CheckboxGroup
             title=""
             items={loadedData.rubros}
             selectedItems={formData.rubroId}
             onChange={handleCheckboxChange}
             name="rubroId"
             showSelectAll={true}
             required={true}
             error={validationErrors.rubros}
             helperText={validationErrors.rubros ? "Debe seleccionar al menos un rubro" : ""}
           />
         </Paper>
       </Grid>
       <Grid item xs={12} md={isSmallScreen ? 12 : 6} lg={isSmallScreen ? 12 : 4}>
         <Paper 
           elevation={3} 
           sx={{ 
             p: { xs: 2, sm: 3 }, 
             mb: 3, 
             borderRadius: 3,
             border: '1px solid',
             borderColor: validationErrors.clientes ? 'error.main' : 'divider'
           }}
         >
           <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
             <Typography variant="h6" color={validationErrors.clientes ? "error" : "inherit"}>
               Clientes
               <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
             </Typography>
             <Button 
               size="small" 
               color="primary" 
               variant="contained"
               startIcon={<AddIcon />}
               onClick={() => handleOpenModal('cliente')}
             >
               Añadir
             </Button>
           </Box>
           <CheckboxGroup
             title=""
             items={loadedData.clientes}
             selectedItems={formData.clienteId}
             onChange={handleCheckboxChange}
             name="clienteId"
             showSelectAll={true}
             required={true}
             error={validationErrors.clientes}
             helperText={validationErrors.clientes ? "Debe seleccionar al menos un cliente" : ""}
             layoutMode="grid"
             columns={2}
           />
         </Paper>
       </Grid>
     </Grid>

     <Accordion>
       <AccordionSummary
         expandIcon={<ExpandMoreIcon />}
         aria-controls="newsletter-content"
         id="newsletter-header"
       >
         <Box sx={{ display: 'flex', alignItems: 'center' }}>
           <PreviewIcon sx={{ mr: 1, color: 'primary.main' }} />
           <Typography variant="h6">
             Vista Previa Newsletter
           </Typography>
         </Box>
       </AccordionSummary>
       <AccordionDetails>
         <Grid container spacing={2}>
           <Grid item xs={12}>
             <Typography variant="subtitle1" color="text.secondary" gutterBottom>
               Personaliza cómo se verá esta normativa en el newsletter
             </Typography>
           </Grid>
           
           <Grid item xs={12}>
             <TextField
               fullWidth
               label="Nombre Abreviado (Newsletter)"
               name="nombreAbreviado"
               value={formData.nombreAbreviado}
               onChange={handleChange} 
               inputProps={{ maxLength: 100}}
               helperText={`${formData.nombreAbreviado.length}/100 caracteres`}
             />
           </Grid>
           
           <Grid item xs={12}>
             <TextField
               fullWidth
               multiline
               rows={4}
               label="Descripción Abreviada (Newsletter)"
               name="descripcionAbreviado"
               value={formData.descripcionAbreviado}
               onChange={handleChange} 
               inputProps={{ maxLength: 400 }}
               helperText={`${formData.descripcionAbreviado.length}/400 caracteres`}
             />
           </Grid>
         </Grid>
       </AccordionDetails>
     </Accordion>

     <Grid item xs={12} sx={{ mt: 2 }}>
       <FormControlLabel
         control={
           <Switch
             name="habilitado"
             checked={formData.habilitado}
             onChange={handleChange}
             color="primary"
           />
         }
         label={formData.habilitado ? "Habilitado" : "Deshabilitado"}
       />
     </Grid>

     {/* Dialog de confirmación mejorado */}
     <Dialog
        open={mostrarDialogoReenvio}
        onClose={() => setMostrarDialogoReenvio(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText>
            Seleccione los clientes a los que desea reenviar esta normativa:
          </DialogContentText>
          
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            borderRadius: 1,
            border: 1,
            borderColor: 'info.main'
          }}>
            <Typography variant="body2" color="info.dark">
              <strong>Atención:</strong> Esta acción eliminará el registro de envío anterior 
              solo para los clientes seleccionados y permitirá enviar la normativa nuevamente.
            </Typography>
          </Box>

          {estadosEnvioClientes.filter(cliente => cliente.fueEnviado).length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ mb: 0, mr: 2 }}>
                  Clientes que recibirán el reenvío:
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        clientesSeleccionadosReenvio.length === 
                        estadosEnvioClientes.filter(cliente => cliente.fueEnviado).length &&
                        clientesSeleccionadosReenvio.length > 0
                      }
                      onChange={handleSeleccionarTodosClientes}
                      size="small"
                    />
                  }
                  label="Seleccionar todos"
                />
              </Box>
              
              <Box sx={{ 
                maxHeight: 250, 
                overflow: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 1
              }}>
                {estadosEnvioClientes
                  .filter(cliente => cliente.fueEnviado)
                  .map((cliente) => (
                    <Box
                      key={cliente.clienteId}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        p: 1,
                        borderRadius: 1,
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={clientesSeleccionadosReenvio.includes(cliente.clienteId)}
                            onChange={(e) => handleSeleccionarClienteReenvio(
                              cliente.clienteId, 
                              e.target.checked
                            )}
                            size="small"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {cliente.clienteNombre}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Enviado: {new Date(cliente.fechaEnvio).toLocaleDateString('es-ES')}
                            </Typography>
                          </Box>
                        }
                        sx={{ width: '100%', margin: 0 }}
                      />
                    </Box>
                  ))}
              </Box>
              
              {clientesSeleccionadosReenvio.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="primary.main">
                    <strong>Clientes seleccionados:</strong> {clientesSeleccionadosReenvio.length}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, gap: 1 }}>    
          <Button 
            onClick={handleReenviarNormativa} 
            variant="contained" 
            color="primary"
            disabled={clientesSeleccionadosReenvio.length === 0}
          >
            Confirmar Reenvío ({clientesSeleccionadosReenvio.length} clientes)
          </Button>
          <Button 
            onClick={() => {
              setMostrarDialogoReenvio(false);
              setClientesSeleccionadosReenvio([]);
            }} 
            variant="contained"
            color="error"
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>


     <Button
       type="submit"
       fullWidth
       variant="contained"
       sx={{ mt: 3, mb: 2 }}
       disabled={isSaving}
     >
       {isSaving ? (
         <>
           <CircularProgress size={24} sx={{ mr: 1, color: 'white' }} />
           Procesando...
         </>
       ) : (
         "Guardar cambios"
       )}
     </Button>

     <ReenvioNormativaDialog
      open={mostrarDialogoReenvioInmediato}
      onClose={() => setMostrarDialogoReenvioInmediato(false)}
      normativaId={id}
      normativaNombre={formData.nombre}
      estadosEnvioClientes={estadosEnvioClientes}
      onReenvioExitoso={handleReenvioInmediatoExitoso}
    />
    
     {/* Modales para agregar nuevos elementos */}
     <MantenedorQuickAddModal
       open={modalState.tipoDocumento}
       onClose={() => handleCloseModal('tipoDocumento')}
       onSubmit={handleAddTipoDocumento}
       title="Nuevo Tipo de Documento"
       fields={tipoDocumentoFields}
       isLoading={quickAddLoading}
     />
     
     <MantenedorQuickAddModal
       open={modalState.organismoEmisor}
       onClose={() => handleCloseModal('organismoEmisor')}
       onSubmit={handleAddOrganismoEmisor}
       title="Nuevo Organismo Emisor"
       fields={organismoEmisorFields}
       isLoading={quickAddLoading}
     />
     
     <MantenedorQuickAddModal
       open={modalState.estadoTramitacion}
       onClose={() => handleCloseModal('estadoTramitacion')}
       onSubmit={handleAddEstadoTramitacion}
       title="Nuevo Estado de Tramitación"
       fields={estadoTramitacionFields}
       isLoading={quickAddLoading}
     />
     
     <MantenedorQuickAddModal
       open={modalState.clasificacion}
       onClose={() => handleCloseModal('clasificacion')}
       onSubmit={handleAddClasificacion}
       title="Nueva Clasificación"
       fields={clasificacionFields}
       isLoading={quickAddLoading}
     />
     
     <MantenedorQuickAddModal
       open={modalState.rubro}
       onClose={() => handleCloseModal('rubro')}
       onSubmit={handleAddRubro}
       title="Nuevo Rubro"
       fields={rubroFields}
       isLoading={quickAddLoading}
     />
     
     <MantenedorQuickAddModal
       open={modalState.cliente}
       onClose={() => handleCloseModal('cliente')}
       onSubmit={handleAddCliente}
       title="Nuevo Cliente"
       fields={clienteFields}
       isLoading={quickAddLoading}
     />

     <MantenedorQuickAddModal
        open={modalState.pais}
        onClose={() => handleCloseModal('pais')}
        onSubmit={handleAddPais}
        title="Nuevo País"
        fields={paisFields}
        isLoading={quickAddLoading}
      />
   </Box>
 );
};

export default EditarNormativa;